import * as React from 'react'
import { PropsWithChildren, ReactElement, useEffect } from 'react'
import {
    Button,
    makeStyles,
    Menu,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    Overflow,
    OverflowItem,
    shorthands,
    Spinner,
    Tab,
    TabList,
    Text,
    useIsOverflowItemVisible,
    useOverflowMenu,
} from '@fluentui/react-components'
import { bundleIcon, MoreHorizontalFilled, MoreHorizontalRegular } from '@fluentui/react-icons'
import { DocketProceedingType } from './Docket/DocketProceedingForm'

export type NavigationState = {
    id: string
    type?: DocketProceedingType
    isNew: boolean
    isDirty: boolean
}

type Props<Entity, State extends NavigationState> = {
    entities: Entity[]
    isLoaded: boolean
    getEntityKey(entity: Entity): React.Key
    getEntityId(entity: Entity): string
    getEntityLabel(entity: Entity): React.ReactNode
    getEntityIcon(entity: Entity): string | number | ReactElement
    selected?: State
    setSelected: (state: State) => void
    createSelectedState(entity: Entity): State
    useColumns: boolean
}

type OverflowMenuProps<Entity, State extends NavigationState> = Props<Entity, State> & {
    onTabSelect: (id: string) => void
}

const MoreHorizontal = bundleIcon(MoreHorizontalFilled, MoreHorizontalRegular)

const useStyles = makeStyles({
    navigation: {
        flexGrow: 1,
        ...shorthands.margin(0, '1px'),
    },
    tab: {
        whiteSpace: 'nowrap',
    },
})

function OverflowMenuItem(props: PropsWithChildren<{ id: string; onTabSelect: () => void }>): ReactElement {
    const isVisible = useIsOverflowItemVisible(props.id)

    if (isVisible) {
        return null
    }

    return (
        <MenuItem key={props.id} onClick={props.onTabSelect}>
            {props.children}
        </MenuItem>
    )
}

function OverflowMenu<Entity, State extends NavigationState>(props: OverflowMenuProps<Entity, State>): ReactElement {
    const { ref, isOverflowing } = useOverflowMenu<HTMLButtonElement>()

    if (!isOverflowing) {
        return null
    }

    return (
        <Menu>
            <MenuTrigger disableButtonEnhancement>
                <Button appearance="transparent" ref={ref} icon={<MoreHorizontal />} role="tab" />
            </MenuTrigger>
            <MenuPopover>
                <MenuList>
                    {props.entities.map(entity => (
                        <OverflowMenuItem
                            key={props.getEntityKey(entity)}
                            id={props.getEntityId(entity)}
                            onTabSelect={() => props.onTabSelect(props.getEntityId(entity))}
                        >
                            {props.getEntityLabel(entity)}
                        </OverflowMenuItem>
                    ))}
                </MenuList>
            </MenuPopover>
        </Menu>
    )
}

export default function Navigation<Entity, State extends NavigationState>(props: Props<Entity, State>): ReactElement {
    const styles = useStyles()
    const orientation = props.useColumns ? 'vertical' : 'horizontal'
    const onTabSelect = (id: string) => {
        const entity: Entity = props.entities.find(entity => props.getEntityId(entity) === id)
        if (props.getEntityId(entity) !== props.selected?.id) {
            props.setSelected(props.createSelectedState(entity))
        }
    }

    // Automatically select the most recent certificate.
    useEffect(() => {
        if (!props.selected && props.entities?.length > 0) {
            onTabSelect(props.getEntityId(props.entities[0]))
        }
    }, [props.entities])

    if (!props.isLoaded) {
        return (
            <div className={styles.navigation}>
                <Spinner size="tiny" label={'Laden...'} labelPosition={props.useColumns ? 'below' : 'after'} />
            </div>
        )
    }

    return (
        // The Overflow component must be forced re-rendered by changing its key property, because it doesn't take
        // sudden orientation changes very well.
        <Overflow minimumVisible={props.useColumns ? 10 : 1} overflowAxis={orientation} key={orientation}>
            <div className={styles.navigation}>
                <TabList
                    vertical={props.useColumns}
                    selectedValue={props.selected?.id || null}
                    onTabSelect={(_, data) => onTabSelect(data.value as string)}
                >
                    {props.selected?.isNew && (
                        <OverflowItem key={props.selected.id} id={props.selected.id} priority={3}>
                            <Tab value={props.selected.id} id={props.selected.id}>
                                <Text italic weight="medium">
                                    Nieuw
                                </Text>
                            </Tab>
                        </OverflowItem>
                    )}
                    {props.entities.map(entity => (
                        <OverflowItem
                            key={props.getEntityKey(entity)}
                            id={props.getEntityId(entity)}
                            priority={props.getEntityId(entity) === props.selected?.id ? 2 : 1}
                        >
                            <Tab
                                value={props.getEntityId(entity)}
                                className={styles.tab}
                                icon={props.getEntityIcon(entity)}
                            >
                                {props.getEntityLabel(entity)}
                            </Tab>
                        </OverflowItem>
                    ))}
                    <OverflowMenu<Entity, State> {...props} onTabSelect={onTabSelect} />
                </TabList>
            </div>
        </Overflow>
    )
}
